import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Activity,
  AppWindow,
  BarChart3,
  Bell,
  Bot,
  GitBranch,
  Home,
  Link2,
  ListChecks,
  ListTodo,
  Lock,
  Puzzle,
  Shield,
  User,
  Users,
  UsersRound,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CenterPanel } from "@/components/layout/center-panel";
import { AccountSettingsContent } from "@/pages/Account";
import { TenantAuthSettings } from "@/pages/tenant/settings/TenantAuthSettings";
import { TenantMcpList } from "@/pages/tenant/settings/TenantMcpList";
import { TenantSettingsOverview } from "@/pages/tenant/settings/TenantSettingsOverview";
import { TenantSkillsSettings } from "@/pages/tenant/settings/TenantSkillsSettings";
import { TenantUsage } from "@/pages/tenant/settings/TenantUsage";
import { TenantVaultSettings } from "@/pages/tenant/settings/TenantVaultSettings";
import { Apps } from "@/pages/tenant/Apps";
import { AppBuilder } from "@/pages/tenant/AppBuilder";
import { AppDetail } from "@/pages/tenant/AppDetail";
import { Agents } from "@/pages/tenant/Agents";
import { NewAgent } from "@/pages/tenant/NewAgent";
import { NewSubagent } from "@/pages/tenant/NewSubagent";
import { AgentDetail } from "@/pages/tenant/AgentDetail";
import { DebugSessions } from "@/pages/tenant/DebugSessions";
import { GroupManagement } from "@/pages/tenant/GroupManagement";
import { Issues } from "@/pages/tenant/Issues";
import { IssueDetail } from "@/pages/tenant/IssueDetail";
import { M365Settings } from "@/pages/tenant/M365Settings";
import { Notifications } from "@/pages/tenant/Notifications";
import { OrgChart } from "@/pages/tenant/OrgChart";
import { Tasks } from "@/pages/tenant/Tasks";
import { UserManagement } from "@/pages/tenant/UserManagement";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { cn } from "@/lib/utils";

type DialogSection =
  | "account"
  | "org"
  | "tenant"
  | "apps"
  | "agents"
  | "tasks"
  | "issues"
  | "users"
  | "groups"
  | "notifications"
  | "m365"
  | "debug";
export type TenantSettingsSection = "overview" | "usage" | "auth" | "vault" | "mcp" | "skills";
type EmbeddedAppView =
  | { kind: "list" }
  | { kind: "detail"; appId: string }
  | { kind: "builder"; appId: string };

const embeddedPageTitleClass = "text-2xl font-semibold text-text-primary";

export type TenantSettingsDialogState =
  | { kind: "account" }
  /** `create` opens that section's create form rather than its list. */
  | { kind: "tenant"; section?: TenantSettingsSection; create?: boolean }
  | { kind: "agent"; agentId?: string }
  | null;

const tenantSections: Array<{
  id: TenantSettingsSection;
  label: string;
  icon: LucideIcon;
}> = [
  { id: "overview", label: "Overview", icon: Home },
  { id: "usage", label: "Usage Metrics", icon: BarChart3 },
  { id: "auth", label: "Authentication", icon: Shield },
  { id: "vault", label: "Key Vault", icon: Lock },
  { id: "mcp", label: "MCP Servers", icon: Puzzle },
  { id: "skills", label: "Skills", icon: Zap },
];

const tenantSettingsNav: Array<
  | {
      id: TenantSettingsSection;
      label: string;
      icon: LucideIcon;
      view?: never;
    }
  | {
      id: "notifications" | "m365";
      label: string;
      icon: LucideIcon;
      view: Extract<DialogSection, "notifications" | "m365">;
    }
  // Notifications and M365 deliberately live only in the outer Admin nav; they
  // are NOT repeated here to avoid a duplicate entry in the tenant sub-nav.
> = [...tenantSections];

export function TenantSettingsDialog({
  state,
  onClose,
}: {
  state: TenantSettingsDialogState;
  onClose: () => void;
}) {
  const {
    tenantId,
    tenantName,
    isTenantAdmin,
    isAgentAdmin,
    isInstanceAdmin,
  } = useTenant();
  const [searchParams] = useSearchParams();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [activeSection, setActiveSection] = useState<DialogSection>("account");
  const [tenantSection, setTenantSection] = useState<TenantSettingsSection>("overview");
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [subagentParentId, setSubagentParentId] = useState<string | null>(null);
  const [appView, setAppView] = useState<EmbeddedAppView>({ kind: "list" });
  const [activeIssueId, setActiveIssueId] = useState<string | null>(null);
  // Set when the caller asked for a create form, cleared as soon as the user
  // moves section, so coming back gives the plain list.
  const [tenantSectionCreate, setTenantSectionCreate] = useState(false);
  const canOpenTenantSettings = isTenantAdmin || isAgentAdmin;
  const availableTenantSections = useMemo(
    () =>
      isTenantAdmin
        ? tenantSections
        : canOpenTenantSettings
          ? tenantSections.filter((item) => item.id === "mcp" || item.id === "skills")
          : [],
    [canOpenTenantSettings, isTenantAdmin],
  );
  const urlAgentId = searchParams.get("agentId");
  const defaultAgentId = useMemo(() => {
    if (urlAgentId && agents.some((agent) => agent.id === urlAgentId)) return urlAgentId;
    return agents[0]?.id ?? null;
  }, [agents, urlAgentId]);
  const selectedAgentId =
    activeAgentId && agents.some((agent) => agent.id === activeAgentId)
      ? activeAgentId
      : null;
  const setSection = (section: DialogSection) => {
    setActiveSection(section);
    setCreatingAgent(false);
    setSubagentParentId(null);
    if (section === "apps") setAppView({ kind: "list" });
    if (section === "issues") setActiveIssueId(null);
  };

  useEffect(() => {
    if (!state) return;

    setSection(state.kind === "agent" ? "agents" : state.kind);
    if (state.kind === "tenant") {
      const requested = state.section ?? availableTenantSections[0]?.id ?? "mcp";
      const allowed = availableTenantSections.some((item) => item.id === requested);
      setTenantSection(allowed ? requested : availableTenantSections[0]?.id ?? "mcp");
      // Only honour the create form on the section that was actually asked for.
      setTenantSectionCreate(allowed ? state.create === true : false);
    }
    if (state.kind === "agent") {
      setActiveAgentId(state.agentId ?? defaultAgentId);
    }
  }, [state, availableTenantSections, defaultAgentId]);

  useEffect(() => {
    if (!state) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onClose]);

  if (!state) return null;

  const showDiagnostics = isInstanceAdmin || isTenantAdmin;
  const isAgentDetailOpen = activeSection === "agents" && !!selectedAgentId;
  const isAgentCreateOpen =
    activeSection === "agents" && (creatingAgent || !!subagentParentId);
  const isFullBleedView =
    isAgentDetailOpen || isAgentCreateOpen || activeSection === "org";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-5">
      <div
        className="absolute inset-0"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative flex h-[min(860px,calc(100vh-36px))] w-[min(1420px,calc(100vw-36px))] overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
        <aside className="flex w-[250px] shrink-0 flex-col border-r border-border bg-sidebar px-2 py-4">
          <nav className="min-h-0 flex-1 overflow-y-auto sidebar-scroll">
            <DialogNavGroup label="Account">
              <DialogNavButton
                active={activeSection === "account"}
                icon={User}
                label="My account"
                onClick={() => setSection("account")}
              />
              <DialogNavButton
                active={activeSection === "org"}
                icon={GitBranch}
                label="Organisation"
                onClick={() => setSection("org")}
              />
            </DialogNavGroup>

            <DialogNavGroup label="Work">
              <DialogNavButton active={activeSection === "apps"} icon={AppWindow} label="Apps" onClick={() => setSection("apps")} />
              <DialogNavButton
                active={activeSection === "agents"}
                icon={Bot}
                label="Agents"
                dataTour="settings-nav-agents"
                onClick={() => {
                  setSection("agents");
                  setActiveAgentId(null);
                }}
              />
              <DialogNavButton active={activeSection === "tasks"} icon={ListTodo} label="Tasks" onClick={() => setSection("tasks")} />
              <DialogNavButton active={activeSection === "issues"} icon={ListChecks} label="Issues" onClick={() => setSection("issues")} />
            </DialogNavGroup>

            {canOpenTenantSettings && (
              <DialogNavGroup label="Admin">
                {isTenantAdmin && (
                  <>
                    <DialogNavButton active={activeSection === "users"} icon={Users} label="Users" onClick={() => setSection("users")} />
                    <DialogNavButton active={activeSection === "groups"} icon={UsersRound} label="Groups" onClick={() => setSection("groups")} />
                    <DialogNavButton active={activeSection === "notifications"} icon={Bell} label="Notifications" onClick={() => setSection("notifications")} />
                    <DialogNavButton active={activeSection === "m365"} icon={Link2} label="M365" onClick={() => setSection("m365")} />
                  </>
                )}
                <DialogNavButton
                  active={activeSection === "tenant"}
                  icon={Shield}
                  label="Tenant settings"
                  onClick={() => setSection("tenant")}
                />
              </DialogNavGroup>
            )}

            {showDiagnostics && (
              <DialogNavGroup label="Diagnostics">
                <DialogNavButton active={activeSection === "debug"} icon={Activity} label="Sessions debug" onClick={() => setSection("debug")} />
              </DialogNavGroup>
            )}
          </nav>
        </aside>

        <section className="relative flex min-w-0 flex-1 flex-col">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4 z-10 h-8 w-8 p-0 text-text-muted hover:text-text-primary"
            onClick={onClose}
            aria-label="Close settings"
            title="Close"
          >
            <X size={16} />
          </Button>
          <div
            className={cn(
              "min-h-0 flex-1",
              isFullBleedView ? "overflow-hidden p-0" : "overflow-y-auto px-10 py-10",
            )}
          >
            <div className={cn("w-full", isFullBleedView ? "h-full" : "mx-auto max-w-[1060px]")}>
              {activeSection === "account" && (
                <CenterPanel title="My account">
                  <AccountSettingsContent showBack={false} showTitle={false} compactAccess />
                </CenterPanel>
              )}
              {activeSection === "org" && <OrgChart />}
              {activeSection === "tenant" && (
                <CenterPanel>
                  <TenantSettingsPanel
                    tenantName={tenantName}
                    availableTenantSections={availableTenantSections}
                    section={tenantSection}
                    setSection={(next) => {
                      setTenantSectionCreate(false);
                      setTenantSection(next);
                    }}
                    startInCreate={tenantSectionCreate}
                    onSelectView={setSection}
                  />
                </CenterPanel>
              )}
              {activeSection === "apps" && appView.kind === "list" && (
                <Apps
                  embedded
                  onOpenApp={(appId) => setAppView({ kind: "detail", appId })}
                  onOpenBuilder={(appId) => setAppView({ kind: "builder", appId })}
                />
              )}
              {activeSection === "apps" && appView.kind === "detail" && (
                <AppDetail
                  appId={appView.appId}
                  onBack={() => setAppView({ kind: "list" })}
                />
              )}
              {activeSection === "apps" && appView.kind === "builder" && (
                <AppBuilder
                  appId={appView.appId}
                  onBack={() => setAppView({ kind: "list" })}
                />
              )}
              {activeSection === "agents" && (
                creatingAgent ? (
                  <NewAgent
                    embedded
                    onClose={() => setCreatingAgent(false)}
                    onCreated={(agentId) => {
                      setCreatingAgent(false);
                      setActiveAgentId(agentId);
                    }}
                  />
                ) : subagentParentId ? (
                  <NewSubagent
                    embedded
                    parentAgentId={subagentParentId}
                    onClose={() => setSubagentParentId(null)}
                    onCreated={(agentId) => {
                      setSubagentParentId(null);
                      setActiveAgentId(agentId);
                    }}
                  />
                ) : selectedAgentId ? (
                  <div className="flex h-full flex-col overflow-hidden">
                    <div className="shrink-0 border-b border-border bg-surface px-4 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => setActiveAgentId(null)}
                      >
                        Back to agents
                      </Button>
                    </div>
                    <div className="min-h-0 flex-1">
                      <AgentDetail agentId={selectedAgentId} embedded />
                    </div>
                  </div>
                ) : (
                  <Agents
                    embedded
                    onOpenAgent={(agentId) => setActiveAgentId(agentId)}
                    onRequestClose={onClose}
                    onCreateInstance={() => setCreatingAgent(true)}
                    onCreateSubagent={(parentId) => setSubagentParentId(parentId)}
                  />
                )
              )}
              {activeSection === "tasks" && <Tasks />}
              {activeSection === "issues" && !activeIssueId && (
                <Issues onOpenIssue={setActiveIssueId} />
              )}
              {activeSection === "issues" && activeIssueId && (
                <IssueDetail issueId={activeIssueId} onBack={() => setActiveIssueId(null)} />
              )}
              {activeSection === "users" && <UserManagement />}
              {activeSection === "groups" && <GroupManagement />}
              {activeSection === "notifications" && <Notifications />}
              {activeSection === "m365" && <M365Settings />}
              {activeSection === "debug" && <DebugSessions />}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function DialogNavGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
        {label}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function TenantSettingsPanel({
  tenantName,
  availableTenantSections,
  section,
  setSection,
  startInCreate = false,
  onSelectView,
}: {
  tenantName: string;
  availableTenantSections: Array<{ id: TenantSettingsSection }>;
  section: TenantSettingsSection;
  setSection: (section: TenantSettingsSection) => void;
  /** Open this section on its create form rather than its list. */
  startInCreate?: boolean;
  onSelectView: (view: DialogSection) => void;
}) {
  return (
    <div>
      <div className="mb-6">
        <h1 className={embeddedPageTitleClass}>Tenant Settings</h1>
        <p className="mt-2 text-sm text-text-secondary">
          Manage settings for{" "}
          <span className="font-medium text-text-primary">{tenantName}</span>.
        </p>
      </div>

      <div className="flex gap-7">
        <nav className="flex w-56 shrink-0 flex-col gap-0.5">
          {tenantSettingsNav
            .filter((item) =>
              item.view ||
              availableTenantSections.some((tenantSection) => tenantSection.id === item.id),
            )
            .map((item) => {
              const Icon = item.icon;
              const isActive = !("externalPath" in item) && section === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-tour={item.id === "mcp" ? "tenant-nav-mcp" : undefined}
                  onClick={() => {
                    if (item.view) {
                      onSelectView(item.view);
                      return;
                    }
                    setSection(item.id);
                  }}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-2 text-left text-[13px] transition-colors",
                    isActive
                      ? "bg-accent/10 font-medium text-accent"
                      : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
                  )}
                >
                  <Icon size={14} />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
        </nav>

        <div className="min-w-0 flex-1">
          <TenantSettingsContent
            section={section}
            setSection={setSection}
            startInCreate={startInCreate}
          />
        </div>
      </div>
    </div>
  );
}

function TenantSettingsContent({
  section,
  setSection,
  startInCreate = false,
}: {
  section: TenantSettingsSection;
  setSection: (section: TenantSettingsSection) => void;
  startInCreate?: boolean;
}) {
  if (section === "usage") return <TenantUsage />;
  if (section === "auth") return <TenantAuthSettings />;
  if (section === "vault") return <TenantVaultSettings />;
  if (section === "mcp") return <TenantMcpList startInCreate={startInCreate} />;
  if (section === "skills") return <TenantSkillsSettings />;
  return <TenantSettingsOverview onOpenSection={setSection} />;
}

function DialogNavButton({
  active = false,
  icon: Icon,
  label,
  onClick,
  dataTour,
}: {
  active?: boolean;
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  dataTour?: string;
}) {
  return (
    <button
      type="button"
      data-tour={dataTour}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors",
        active
          ? "bg-accent/10 font-medium text-accent"
          : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
      )}
    >
      <Icon size={14} />
      <span className="truncate">{label}</span>
    </button>
  );
}
